import { IconMapPin16, IconProductPanorama36016 } from '@cian/ui-kit-design-tokens/icons';
import { Tabs } from '@cian/ui-kit/tabs';
import { useCallback, useState } from 'react';

import type { IYmapsKeys, TYmapsCoordinates } from 'shared/utils/ymaps';

import { ObjectOnMap } from './components/ObjectOnMap';
import { ObjectOnPanorama } from './components/ObjectOnPanorama';
import { PanoramaNotFound } from './components/PanoramaNotFound';
import style from './style.css';

/** Параметры карты как на проде (`renderObjectMapView` в `onload.html` монолита) */
const MAP_ZOOM = 13;

const TABS = [
  { text: 'Карта', icon: IconMapPin16, dataTestId: 'MapTab' },
  { text: 'Панорамы', icon: IconProductPanorama36016, dataTestId: 'PanoramaTab' },
];

type TPanoramaStatus = 'unmounted' | 'loading' | 'loaded' | 'notFound';

export interface IBuildingMapProps {
  coordinates: TYmapsCoordinates;
  keys: IYmapsKeys;
  /** Сразу под техническими характеристиками — без верхнего отступа */
  isAfterTech: boolean;
  onError(error: unknown): void;
}

/**
 * Блок «Карта / Панорамы» — `object_map_view.tsx` монолита.
 * Панорама монтируется при первом открытии вкладки и дальше не пересоздаётся
 */
export const BuildingMap = ({ coordinates, keys, isAfterTech, onError }: IBuildingMapProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [panoramaStatus, setPanoramaStatus] = useState<TPanoramaStatus>('unmounted');
  const isPanoramaTab = activeTabIndex === 1;

  const handleTabClick = (index: number) => {
    setActiveTabIndex(index);

    if (index === 1 && panoramaStatus === 'unmounted') {
      setPanoramaStatus('loading');
    }
  };

  const handlePanoramaLoad = useCallback(
    (isLoaded: boolean) => setPanoramaStatus(isLoaded ? 'loaded' : 'notFound'),
    [],
  );

  return (
    <section
      id="map"
      className={`${style['container']} ${isAfterTech ? style['container--after-tech'] : ''}`}
      data-name="BuildingMap"
    >
      <div className={style['tabs']}>
        <Tabs items={TABS} activeTabIndex={activeTabIndex} onTabClick={handleTabClick} />
      </div>
      <div hidden={isPanoramaTab}>
        <ObjectOnMap coordinates={coordinates} zoom={MAP_ZOOM} keys={keys} onError={onError} />
      </div>
      <div hidden={!isPanoramaTab}>
        {panoramaStatus === 'notFound' ? (
          <PanoramaNotFound onBackClick={() => setActiveTabIndex(0)} />
        ) : (
          panoramaStatus !== 'unmounted' && (
            <ObjectOnPanorama coordinates={coordinates} keys={keys} onLoad={handlePanoramaLoad} onError={onError} />
          )
        )}
      </div>
    </section>
  );
};
